import { useEffect, useRef, useState } from "react";

export interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmPhrase: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /**
   * True while the confirmed action is in flight. Disables both buttons and
   * blocks Escape / backdrop dismissal so a slow request can't be fired twice
   * (a double-click previously created two submissions). The parent owns this
   * flag and clears it when the async work settles.
   */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Modal that requires the user to type a literal phrase to confirm. Used
 * anywhere a misclick on PROD would cause real damage.
 */
export function ConfirmDialog({
  title,
  message,
  confirmPhrase,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // While the action is in flight, swallow Escape so the operator can't
      // dismiss the dialog mid-request (the request keeps running regardless).
      if (e.key === "Escape" && !busy) onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, busy]);

  const matches = input.trim() === confirmPhrase;

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onCancel}>
      <div
        className={"modal" + (danger ? " modal-danger" : "")}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2 className="modal-title">{title}</h2>
        <p className="modal-message">{message}</p>
        <label className="modal-label">
          Type <code>{confirmPhrase}</code> to confirm:
          <input
            ref={inputRef}
            className="modal-input mono"
            type="text"
            value={input}
            disabled={busy}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && matches && !busy) onConfirm();
            }}
            spellCheck={false}
            autoComplete="off"
          />
        </label>
        <div className="modal-actions">
          <button className="btn" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            className={"btn " + (danger ? "btn-danger" : "btn-primary")}
            disabled={!matches || busy}
            onClick={onConfirm}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
